import type { BillingAccount, BillingStatusResponse } from "@hector/shared-types";

/** Actions de facturation proposées à un admin, dérivées du statut en cache. */
export interface BillingActions {
  /** « Régler mon abonnement » : seulement s'il n'existe aucun abonnement Stripe vivant. */
  canCheckout: boolean;
  /** « Ajouter des licences » : abonnement vivant en `active`, au prix de CET abonnement. */
  canAddSeats: boolean;
  /** « Passer à l'annuel » : seulement depuis le prix mensuel standard, et tant qu'aucun changement n'est déjà programmé. */
  canSwitchAnnual: boolean;
}

/**
 * Matrice de décision des boutons de facturation (onglet Facturation, modale
 * d'invitation, badge de statut).
 *
 * Aucune information sur la grille tarifaire du cabinet (nouveau tarif, ancien
 * tarif, tarif personnalisé) n'est affichée à l'utilisateur, par décision
 * produit : le backend ne l'expose même pas. Les boutons dépendent seulement de
 * l'existence d'un abonnement vivant et, pour l'annuel, du prix mensuel en vigueur.
 *
 * WHY `has_live_subscription` et pas `plan` : `plan` vaut `null` dès que le prix
 * n'est pas l'un des 3 prix configurés, donc pour chaque cabinet à tarif négocié.
 * S'en servir comme « pas encore abonné » proposait un checkout à des cabinets
 * qui paient déjà, et un clic ouvrait un 2ᵉ abonnement au tarif standard (#1079).
 * Le backend refuse désormais ce checkout ; cette matrice évite de le proposer.
 *
 * Chaque règle recopie la garde serveur correspondante : un bouton affiché mais
 * refusé par l'API serait un piège.
 *  - add-seats exige un abonnement `active` ou `trialing` (`services/billing/seats.ts`) ;
 *  - le passage à l'annuel exige le prix mensuel standard, aucun changement déjà
 *    programmé et un abonnement qui ne doit pas prendre fin (`planSwitch.ts`) ;
 *  - un abonnement vivant non `active` (`past_due`, `unpaid`, `incomplete`) se
 *    règle par le portail Stripe, jamais par un nouveau checkout.
 */
/**
 * Statuts où l'on peut ajouter des licences — même règle que `services/billing/seats.ts` :
 * `active`, et `trialing` (un cabinet en essai de 30 jours dimensionne son équipe pendant l'essai).
 */
const isSeatAddable = (billingStatus: string): boolean => billingStatus === "active" || billingStatus === "trialing";

export function billingActions(status: BillingStatusResponse): BillingActions {
  // Facturation gérée par un autre espace du cabinet (#1835) : aucune action ici,
  // l'écran nomme la personne à contacter. Un checkout ouvrirait un second
  // abonnement bien réel pour un cabinet qui paie déjà.
  if (status.billing_manager) {
    return { canCheckout: false, canAddSeats: false, canSwitchAnnual: false };
  }
  const live = status.has_live_subscription;
  // Plusieurs comptes de facturation : les actions se font compte par compte
  // (`accountActions`), l'API refuse un ajout qui ne dit pas sur lequel.
  const single = status.accounts.length <= 1;
  // Ajout de licences fermé au self-serve tant que le produit ne l'ouvre pas : la
  // licence en plus est posée par l'équipe Hector dans Stripe.
  const addSeats = status.self_serve_add_seats;
  return {
    canCheckout: !live,
    canAddSeats: addSeats && single && live && isSeatAddable(status.billing_status),
    canSwitchAnnual:
      single && live && status.plan === "monthly" && !status.pending_plan_change && !status.cancel_at_period_end,
  };
}

/** Actions proposées sur UN compte de facturation d'un cabinet qui en a plusieurs. */
export interface AccountActions {
  canOpenPortal: boolean;
  canAddSeats: boolean;
  canSwitchAnnual: boolean;
}

/**
 * Mêmes règles que `billingActions`, appliquées à un compte : seul un admin du
 * cabinet ou le membre désigné sur ce compte (`can_manage`, décidé par le
 * serveur) peut agir dessus.
 */
export function accountActions(account: BillingAccount, selfServeAddSeats: boolean): AccountActions {
  return {
    canOpenPortal: account.can_manage,
    canAddSeats: selfServeAddSeats && account.can_manage && isSeatAddable(account.billing_status),
    canSwitchAnnual:
      account.can_manage && account.plan === "monthly" && !account.pending_plan_change && !account.cancel_at_period_end,
  };
}

/**
 * L'onglet Paramètres → Facturation est-il affiché à ce membre ?
 *
 * Tout membre qui consomme une licence le voit (en lecture seule s'il n'est pas
 * admin). Un membre EXEMPTÉ de licence — équipe Hector, mais aussi client sous
 * forfait négocié hors Stripe — n'a rien à y lire : l'onglet n'apparaît que s'il
 * a un abonnement Stripe vivant à gérer pour les autres membres du cabinet
 * (admin du cabinet, ou membre désigné sur un compte de facturation).
 *
 * `status` absent (chargement, échec) : un exempté ne voit pas l'onglet — il
 * apparaît dès que le statut confirme qu'il y a quelque chose à gérer.
 */
export function isBillingTabVisible(
  isExempt: boolean,
  status: BillingStatusResponse | null | undefined,
): boolean {
  if (!isExempt) return true;
  if (!status || status.billing_manager || !status.has_live_subscription) return false;
  return status.is_admin || status.accounts.some((account) => account.can_manage);
}
